import { useState, useRef } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import {
  FileText,
  Database,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Lock,
  RefreshCw,
  User,
  Phone,
  Table as TableIcon,
  TrendingUp,
  MessageSquare,
  Sparkles,
  ChevronDown
} from 'lucide-react'
import { CATALOG_DETAILS } from '@/data/catalog-details'
import type { DataAsset } from '@/types'

interface CatalogDetailProps {
  catalogId: string
  onBack: () => void
  onReportQuery: (asset: DataAsset, templateId?: string) => void
  authorized?: boolean
}

export function CatalogDetailPage({ catalogId, onBack, onReportQuery, authorized = false }: CatalogDetailProps) {
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authDescription, setAuthDescription] = useState('')
  const [showQualityModal, setShowQualityModal] = useState(false)
  const [showAIChatModal, setShowAIChatModal] = useState(false) // 已隐藏
  const [selectedField, setSelectedField] = useState<{ id: string; name: string } | null>(null)
  const [expandedField, setExpandedField] = useState<string | null>(null)
  const [qualityFeedback, setQualityFeedback] = useState('')
  const fieldRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const detail = CATALOG_DETAILS[catalogId]

  // 默认展开第一个字段
  if (!expandedField && detail && detail.fields.length > 0) {
    setExpandedField(detail.fields[0].id)
  }

  if (!detail) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-gray-50">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-lg font-medium text-gray-700">未找到该业务主题的详情信息</h2>
        <Button onClick={onBack} className="mt-4" variant="outline">
          返回首页
        </Button>
      </div>
    )
  }

  const handleAuthSubmit = () => {
    console.log('提交权限申请:', authDescription)
    setShowAuthModal(false)
  }

  const handleAnalysis = () => {
    if (authorized) {
      // 代发客群主题使用代发报表模板(11)，其他使用默认模板
      const templateId = catalogId === 'retail-customer' ? '11' : undefined
      onReportQuery({
        id: 'catalog-' + catalogId,
        name: detail.name,
        department: detail.owner,
        owner: detail.contact,
        path: detail.name,
        authorized: true,
        viewCount: 0,
        likeCount: 0,
        commentCount: 0,
        roles: [],
        catalogPath: [detail.name],
        catalogId: catalogId,
        description: detail.description
      }, templateId)
    }
  }

  const handleFieldClick = (fieldId: string, fieldName: string) => {
    setSelectedField({ id: fieldId, name: fieldName })
    setExpandedField(fieldId) // 设置当前字段为展开状态
    const element = fieldRefs.current[fieldId]
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleQualitySubmit = () => {
    console.log('提交质量反馈:', { field: selectedField, feedback: qualityFeedback })
    setShowQualityModal(false)
    setQualityFeedback('')
    setSelectedField(null)
    // 这里可以调用 API 提交反馈
  }

  const openQualityModal = (fieldId: string, fieldName: string) => {
    setSelectedField({ id: fieldId, name: fieldName })
    setQualityFeedback('')
    setShowQualityModal(true)
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* 顶部标题栏 */}
      <div className="h-11 border-b bg-white flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <Button onClick={onBack} variant="ghost" size="sm" className="h-7 px-2 text-xs">
            ← 返回
          </Button>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-gcb-primary" />
            <h1 className="font-semibold text-gray-900 text-sm">{detail.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!authorized && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAuthModal(true)}
              className="h-8 px-2.5 text-xs border-gcb-primary/60 text-gcb-primary hover:bg-blue-50"
            >
              <Lock className="w-3.5 h-3.5 mr-1" />
              申请权限
            </Button>
          )}
          {authorized && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAnalysis}
                className="h-8 px-2.5 text-xs border-gcb-primary text-gcb-primary hover:bg-blue-50"
              >
                <BarChart3 className="w-3.5 h-3.5 mr-1" />
                自主分析
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 主体内容 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 左侧主题信息 */}
        <div className="w-72 border-r bg-white overflow-y-auto">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900 text-sm mb-2">{detail.name}</h2>
            <p className="text-xs text-gray-600 leading-relaxed">{detail.description}</p>
          </div>

          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <TableIcon className="w-4 h-4 text-gcb-primary" />
              <span className="text-gray-500">表数量：</span>
              <span className="font-medium">{detail.tableCount}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <Database className="w-4 h-4 text-gcb-primary" />
              <span className="text-gray-500">记录数：</span>
              <span className="font-medium">{detail.recordCount}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <RefreshCw className="w-4 h-4 text-gcb-primary" />
              <span className="text-gray-500">更新频率：</span>
              <span className="font-medium">{detail.updateFrequency}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <User className="w-4 h-4 text-gcb-primary" />
              <span className="text-gray-500">负责人：</span>
              <span className="font-medium">{detail.owner}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <Phone className="w-4 h-4 text-gcb-primary" />
              <span className="text-gray-500">联系方式：</span>
              <span className="font-medium">{detail.contact}</span>
            </div>
          </div>

          <div className="p-4 border-t">
            <h3 className="text-xs font-semibold text-gray-700 mb-3">字段列表 ({detail.fields.length})</h3>
            <ScrollArea className="h-64">
              <div className="space-y-1">
                {detail.fields.map((field) => (
                  <div
                    key={field.id}
                    className="p-2 hover:bg-blue-50 rounded-md cursor-pointer transition-colors"
                    onClick={() => handleFieldClick(field.id, field.name)}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-700 truncate">{field.name}</span>
                    </div>
                    <span className="text-[10px] text-gray-400 ml-5">{field.code}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* 右侧字段详情 */}
        <ScrollArea className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6 space-y-4">
            {detail.fields.map((field) => {
              const isExpanded = expandedField === field.id
              return (
                <div
                  key={field.id}
                  ref={(el) => { fieldRefs.current[field.id] = el }}
                  id={`field-${field.id}`}
                  className="bg-white rounded-lg border shadow-sm overflow-hidden scroll-mt-20"
                >
                  {/* 字段标题 - 可点击展开/折叠 */}
                  <button
                    onClick={() => setExpandedField(isExpanded ? null : field.id)}
                    className="w-full h-10 bg-gradient-to-r from-gray-50 to-gray-100 border-b px-4 flex items-center justify-between hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gcb-primary" />
                      <h3 className="font-semibold text-gray-900 text-sm">{field.name}</h3>
                      <span className="text-xs text-gray-400 font-mono">{field.code}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={field.nullable ? 'outline' : 'default'} className="h-5 text-xs">
                        {field.nullable ? '可为空' : '非空'}
                      </Badge>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* 字段详情内容 - 展开时显示 */}
                  {isExpanded && (
                    <div className="p-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                      {/* 基本信息 */}
                      <div className="grid grid-cols-4 gap-4 text-xs">
                        <div>
                          <span className="text-gray-500">数据类型</span>
                          <p className="font-medium text-gray-700 mt-1">{field.dataType}</p>
                        </div>
                        {field.length && (
                          <div>
                            <span className="text-gray-500">字段长度</span>
                            <p className="font-medium text-gray-700 mt-1">{field.length}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">更新频率</span>
                          <p className="font-medium text-gray-700 mt-1">{field.updateFrequency}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">源表</span>
                          <p className="font-medium text-gray-700 mt-1 truncate" title={field.sourceTable}>{field.sourceTable}</p>
                        </div>
                      </div>

                      {/* 业务定义 */}
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-gcb-primary" />
                          <span className="text-xs font-semibold text-gray-700">业务定义</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed bg-blue-50 rounded-md p-3">
                          {field.definition}
                        </p>
                      </div>

                      {/* 统计口径 */}
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-gcb-primary" />
                          <span className="text-xs font-semibold text-gray-700">统计口径</span>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed bg-green-50 rounded-md p-3">
                          {field.caliber}
                        </p>
                      </div>

                      {/* 数据质量 */}
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-gcb-primary" />
                            <span className="text-xs font-semibold text-gray-700">数据质量</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openQualityModal(field.id, field.name)}
                            className="h-6 text-xs border-gcb-primary/60 text-gcb-primary hover:bg-blue-50"
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            反馈质量问题
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-gcb-primary">{field.quality.completeness}%</div>
                            <div className="text-xs text-gray-500 mt-1">完整性</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-gcb-primary">{field.quality.accuracy}%</div>
                            <div className="text-xs text-gray-500 mt-1">准确性</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-gcb-primary">{field.quality.timeliness}%</div>
                            <div className="text-xs text-gray-500 mt-1">及时性</div>
                          </div>
                        </div>
                        {field.quality.issues.length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <span className="text-xs font-medium text-amber-700">已知质量问题</span>
                            </div>
                            <ul className="space-y-1">
                              {field.quality.issues.map((issue, index) => (
                                <li key={index} className="text-xs text-amber-700 flex items-start gap-1.5">
                                  <span className="w-1 h-1 rounded-full bg-amber-500 mt-1 shrink-0" />
                                  {issue}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* 示例数据 */}
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Database className="w-4 h-4 text-gcb-primary" />
                          <span className="text-xs font-semibold text-gray-700">示例数据</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {field.sampleData.map((sample, index) => (
                            <Badge key={index} variant="outline" className="font-mono text-xs bg-gray-50">
                              {sample}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </ScrollArea>
      </div>

      {/* 申请权限弹窗 */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>申请数据权限</DialogTitle>
            <DialogDescription>
              申请访问"{detail.name}"业务主题的数据权限，请填写申请事由。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                申请事由 <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="请详细说明申请权限的原因、使用场景、预期用途等"
                value={authDescription}
                onChange={(e) => setAuthDescription(e.target.value)}
                className="w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gcb-primary focus:outline-none focus:ring-1 focus:ring-gcb-primary"
                autoFocus
              />
            </div>
            <div className="text-xs text-gray-500 bg-gray-50 rounded-md p-3">
              <p className="font-medium mb-1">权限审批流程：</p>
              <p>1. 部门负责人审批 → 2. 数据管理部门审批 → 3. 授予权限</p>
              <p className="mt-1">预计审批时间：1-2 个工作日</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAuthModal(false)} className="h-10 px-4">
              取消
            </Button>
            <Button
              onClick={handleAuthSubmit}
              className="h-10 px-6 bg-gcb-primary hover:bg-gcb-primaryDark"
              disabled={!authDescription.trim()}
            >
              提交申请
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 反馈质量问题弹窗 */}
      <Dialog open={showQualityModal} onOpenChange={setShowQualityModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>反馈数据质量问题</DialogTitle>
            <DialogDescription>
              字段：{selectedField?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                问题描述 <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="请详细描述您发现的数据质量问题，例如：数据缺失、数据错误、数据不一致等"
                value={qualityFeedback}
                onChange={(e) => setQualityFeedback(e.target.value)}
                className="w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-gcb-primary focus:outline-none focus:ring-1 focus:ring-gcb-primary"
                autoFocus
              />
            </div>
            <div className="text-xs text-gray-500 bg-blue-50 rounded-md p-3">
              <p className="font-medium mb-1">提交后我们将：</p>
              <p>1. 记录您反馈的问题</p>
              <p>2. 数据管理团队将尽快核查处理</p>
              <p>3. 处理结果将通过消息通知您</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowQualityModal(false)} className="h-10 px-4">
              取消
            </Button>
            <Button
              onClick={handleQualitySubmit}
              className="h-10 px-6 bg-gcb-primary hover:bg-gcb-primaryDark"
              disabled={!qualityFeedback.trim()}
            >
              一键提交
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI 问数弹窗 - 已隐藏 */}
      {/* <AIChatModal
        open={showAIChatModal}
        onOpenChange={setShowAIChatModal}
        catalogId={catalogId}
        catalogName={detail.name}
        onSaveToSpace={(result, question) => {
          console.log('保存到空间:', { result, question })
          alert('结果已保存到"我的空间"')
          setShowAIChatModal(false)
        }}
      /> */}
    </div>
  )
}
